import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EnumSelect,
  Input,
  Label,
  TOUCH,
} from "@nymbl/nos2-rules";
import { useState } from "react";
import { TaskSearch } from "@/components/time/TaskSearch";
import { useCreateRule, useUpdateRule } from "@/hooks/use-calendar";
import {
  CALENDAR_RULE_KIND_LABELS,
  MANUAL_CALENDAR_RULE_KIND_OPTIONS,
  MANUAL_CALENDAR_RULE_KINDS,
  type ManualCalendarRuleKind,
} from "@/lib/enums";
import { ApiError } from "@/lib/time/api-client";
import { projectLine, taskLabel } from "@/lib/time/labels";
import { cn } from "@/lib/utils";
import type { CalendarRuleView } from "@/schemas/calendar";
import type { AssignableTask } from "@/schemas/time";

/** The server's "not assigned" refusal is about the task; every other refusal is about the text. */
const NOT_ASSIGNED_STATUS = 409;

const TEXT_LABELS: Record<ManualCalendarRuleKind, string> = {
  title_exact: "Title",
  title_contains: "Words in the title",
  domain: "Domain",
};

const TEXT_ERROR_ID = "rule-text-error";
const TASK_ERROR_ID = "rule-task-error";

interface RuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Null adds a rule; a rule edits it. The parent remounts with a new `key` per opening. */
  rule: CalendarRuleView | null;
}

/** One form for Add rule and Edit rule: Type, what to match, and the task (Log time's picker). */
export function RuleDialog({ open, onOpenChange, rule }: RuleDialogProps) {
  const create = useCreateRule();
  const update = useUpdateRule();
  const series = rule?.kind === "series";
  const [kind, setKind] = useState<ManualCalendarRuleKind>(
    MANUAL_CALENDAR_RULE_KINDS.find((k) => k === rule?.kind) ?? "title_contains",
  );
  const [text, setText] = useState(rule?.label ?? "");
  const [task, setTask] = useState<AssignableTask | null>(rule?.task ?? null);
  const [query, setQuery] = useState("");
  const [error, setError] = useState<{ field: "text" | "task"; message: string } | null>(null);
  const saving = create.isPending || update.isPending;
  const dirty =
    text !== (rule?.label ?? "") ||
    task?.demandPersonId !== rule?.task?.demandPersonId ||
    (rule === null && kind !== "title_contains");
  const canSave = !saving && task !== null && (series || text.trim().length > 0);

  const save = async () => {
    if (!task) return;
    setError(null);
    const pick = { projectTaskId: task.projectTaskId, demandPersonId: task.demandPersonId };
    try {
      if (!rule) await create.mutateAsync({ kind, text, ...pick });
      else if (series) await update.mutateAsync({ id: rule.id, ...pick });
      else await update.mutateAsync({ id: rule.id, kind, text, ...pick });
      onOpenChange(false);
    } catch (err) {
      // The hook already toasted; the field says it too.
      const message = err instanceof Error ? err.message : "Couldn't save the rule";
      const onTask = series || (err instanceof ApiError && err.status === NOT_ASSIGNED_STATUS);
      setError({ field: onTask ? "task" : "text", message });
    }
  };

  const textError = error?.field === "text" ? error.message : null;
  const taskError = error?.field === "task" ? error.message : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} dirty={dirty}>
      <DialogContent size="lg">
        <DialogHeader className="text-left">
          <DialogTitle>{rule ? "Edit rule" : "Add rule"}</DialogTitle>
          <DialogDescription className="sr-only">
            Pick how the rule matches a meeting, what it looks for and the task to log it to.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) void save();
          }}
        >
          <DialogBody className="space-y-5">
            <div className="space-y-2">
              <span className="text-sm font-medium" aria-hidden={!series}>
                Type
              </span>
              {series ? (
                <p className="text-sm">{CALENDAR_RULE_KIND_LABELS.series}</p>
              ) : (
                <EnumSelect
                  value={kind}
                  onValueChange={(v) => {
                    const next = MANUAL_CALENDAR_RULE_KINDS.find((k) => k === v);
                    if (next) setKind(next);
                    setError(null);
                  }}
                  options={MANUAL_CALENDAR_RULE_KIND_OPTIONS}
                  aria-label="Type"
                  className={cn(TOUCH, "w-full")}
                />
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="rule-text">{series ? "Meeting series" : TEXT_LABELS[kind]}</Label>
              <Input
                id="rule-text"
                autoComplete="off"
                value={text}
                readOnly={series}
                onChange={(e) => {
                  setText(e.target.value);
                  if (error?.field === "text") setError(null);
                }}
                placeholder={kind === "domain" && !series ? "e.g. crye.com" : undefined}
                aria-invalid={textError !== null}
                aria-describedby={textError ? TEXT_ERROR_ID : undefined}
                className={cn(TOUCH, textError && "border-danger", series && "bg-muted")}
              />
              {textError && (
                <p id={TEXT_ERROR_ID} role="alert" className="text-sm font-semibold text-danger">
                  {textError}
                </p>
              )}
            </div>

            <div className="space-y-2">
              {task ? (
                <>
                  <span className="text-sm font-medium">Task</span>
                  <div
                    className={cn(
                      "flex items-start justify-between gap-3 rounded-lg bg-muted p-3",
                      taskError && "ring-1 ring-danger",
                    )}
                  >
                    <div className="min-w-0 space-y-1">
                      <div className="font-semibold">{task.taskName}</div>
                      <div className="text-xs text-muted-foreground">
                        {projectLine(task.projectName, task.accountName)}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={TOUCH}
                      aria-label={`Change task ${taskLabel(task.taskName, task.projectName)}`}
                      aria-describedby={taskError ? TASK_ERROR_ID : undefined}
                      onClick={() => {
                        setQuery("");
                        setTask(null);
                        setError(null);
                      }}
                    >
                      Change
                    </Button>
                  </div>
                </>
              ) : (
                <TaskSearch
                  inputId="rule-q"
                  label="Task"
                  query={query}
                  onQueryChange={setQuery}
                  onPick={(picked) => {
                    setTask(picked);
                    setError(null);
                  }}
                />
              )}
              {taskError && (
                <p id={TASK_ERROR_ID} role="alert" className="text-sm font-semibold text-danger">
                  {taskError}
                </p>
              )}
            </div>
          </DialogBody>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              className={TOUCH}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" className={TOUCH} disabled={!canSave}>
              {rule ? "Save" : "Add rule"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
